import { useId, useRef, useState } from "react";

import {
  reviewAdminAccessRequest,
  revokeAdminLiveAccess,
  type AdminAccessRequest,
  type AdminReviewStatus,
} from "@/lib/api/admin-access-requests";
import {
  formatReviewedMeta,
  statusToLabel,
} from "@/lib/api/admin-access-request-labels";
import { ApiErrorNotice } from "@/lib/api/portal-error-notice";
import {
  ADMIN_REVIEW_FAILURE,
  ADMIN_REVOKE_FAILURE,
  ADMIN_ACTION_ERROR_MESSAGES,
  presentApiError,
  type PresentedApiError,
} from "@/lib/api/portal-error-copy";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { useLiveCredentialsEnabled } from "@/lib/feature-flags/useSandboxCredentialsEnabled";
import { LiveApproveDialog } from "@/pages/admin/LiveApproveDialog";
import { LiveRevokeDialog } from "@/pages/admin/LiveRevokeDialog";

const MIN_REJECTION_REASON_LENGTH = 10;
const MAX_REJECTION_REASON_LENGTH = 1000;

type ReviewActionsProps = {
  request: AdminAccessRequest;
  onReviewed: (request: AdminAccessRequest) => void;
};

type ConfirmMode = AdminReviewStatus | null;

function pendingBanner(wasContested: boolean) {
  return (
    <div className="review-banner pending" data-testid="admin-review-banner">
      <i className="ti ti-clock" aria-hidden="true" />
      <span>
        {wasContested
          ? "Pending review — applicant contested the previous decision."
          : "Pending review — no action taken yet."}
      </span>
    </div>
  );
}

function revokedBanner() {
  return (
    <div className="review-banner rejected" data-testid="admin-review-banner">
      <i className="ti ti-circle-x" aria-hidden="true" />
      <span data-testid="admin-live-revoke-explanation">
        Live access revoked. The granted locations or namespaces were removed.
        The integrator record remains.
      </span>
    </div>
  );
}

function decidedBanner(request: AdminAccessRequest) {
  const label = statusToLabel(request.status);
  const meta = formatReviewedMeta(
    request.reviewed_at,
    request.reviewed_by_name,
  );
  const detail = meta ? `${label} — ${meta}` : label;
  return (
    <div
      className={`review-banner ${request.status}`}
      data-testid="admin-review-banner"
    >
      <i
        className={`ti ${request.status === "approved" ? "ti-circle-check" : "ti-circle-x"}`}
        aria-hidden="true"
      />
      <span>{detail}</span>
    </div>
  );
}

function trimmedReasonLength(value: string): number {
  return value.trim().length;
}

export function ReviewActions({ request, onReviewed }: ReviewActionsProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<PresentedApiError | null>(null);
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonDirty, setReasonDirty] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const reasonId = useId();
  const reasonHintId = useId();
  const reasonErrorId = useId();

  const isPending = request.status === "pending";
  const liveCredentialsEnabled = useLiveCredentialsEnabled();
  const showLiveApprove =
    liveCredentialsEnabled &&
    request.environment === "live" &&
    request.live_scope != null;
  const showLiveRevoke =
    liveCredentialsEnabled &&
    request.environment === "live" &&
    request.status === "approved" &&
    request.origin === "portal" &&
    Boolean(request.integrator_id?.trim()) &&
    request.fulfillment === "provisioned" &&
    request.live_scope != null;
  const wasContested = Boolean(request.contested_at);
  const trimLen = trimmedReasonLength(reason);
  const reasonTooShort = trimLen > 0 && trimLen < MIN_REJECTION_REASON_LENGTH;
  const showReasonError = reasonDirty && reasonTooShort;
  const canDeny = trimLen >= MIN_REJECTION_REASON_LENGTH;

  function closeModal() {
    if (submitting) {
      return;
    }
    setConfirmMode(null);
    setRevokeOpen(false);
    setReason("");
    setReasonDirty(false);
    setError(null);
  }

  const standardModal =
    confirmMode !== null && !(showLiveApprove && confirmMode === "approved");
  useDialogFocusTrap(standardModal, modalRef, closeModal);

  async function submitReview(
    status: AdminReviewStatus,
    confirmation?: string,
  ) {
    if (submitting || !isPending) {
      return;
    }

    if (status === "rejected" && !canDeny) {
      setReasonDirty(true);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const updated = await reviewAdminAccessRequest(
        request.id,
        status,
        status === "rejected" ? reason.trim() : undefined,
        confirmation,
      );
      setConfirmMode(null);
      setReason("");
      setReasonDirty(false);
      onReviewed(updated);
    } catch (err) {
      setError(
        presentApiError(err, {
          generic: ADMIN_REVIEW_FAILURE,
          byCode: ADMIN_ACTION_ERROR_MESSAGES,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function submitRevoke() {
    if (submitting || !showLiveRevoke) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const updated = await revokeAdminLiveAccess(request.id, "Revoke");
      setRevokeOpen(false);
      onReviewed(updated);
    } catch (err) {
      setError(
        presentApiError(err, {
          generic: ADMIN_REVOKE_FAILURE,
          byCode: ADMIN_ACTION_ERROR_MESSAGES,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="review-actions" data-testid="admin-review-actions">
      {request.fulfillment === "revoked"
        ? revokedBanner()
        : isPending
          ? pendingBanner(wasContested)
          : decidedBanner(request)}

      {request.rejection_reason ? (
        <div
          className="review-rejection-reason"
          data-testid="admin-review-rejection-reason"
        >
          <span className="review-rejection-reason__label">
            {wasContested && isPending ? "Prior reason" : "Reason"}
          </span>
          <p className="review-rejection-reason__text">
            {request.rejection_reason}
          </p>
        </div>
      ) : null}

      {request.contestation_message ? (
        <div
          className="review-rejection-reason"
          data-testid="admin-review-contestation-message"
        >
          <span className="review-rejection-reason__label">
            Contestation message
          </span>
          <p className="review-rejection-reason__text">
            {request.contestation_message}
          </p>
        </div>
      ) : null}

      {isPending ? (
        <div className="review-buttons">
          <button
            type="button"
            className="review-btn approve"
            data-testid="admin-review-approve"
            disabled={submitting}
            onClick={() => {
              setError(null);
              setConfirmMode("approved");
            }}
          >
            <i className="ti ti-circle-check" aria-hidden="true" />
            Approve
          </button>
          <button
            type="button"
            className="review-btn deny"
            data-testid="admin-review-deny"
            disabled={submitting}
            onClick={() => {
              setError(null);
              setReason("");
              setReasonDirty(false);
              setConfirmMode("rejected");
            }}
          >
            <i className="ti ti-circle-x" aria-hidden="true" />
            Deny
          </button>
        </div>
      ) : null}

      {showLiveRevoke ? (
        <div className="review-buttons">
          <button
            type="button"
            className="review-btn deny"
            data-testid="admin-review-revoke"
            disabled={submitting}
            onClick={() => {
              setError(null);
              setRevokeOpen(true);
            }}
          >
            <i className="ti ti-circle-x" aria-hidden="true" />
            Revoke
          </button>
        </div>
      ) : null}

      {error && confirmMode === null && !revokeOpen ? (
        <ApiErrorNotice
          error={error}
          className="notes-error"
          testId="admin-review-error"
        />
      ) : null}

      {showLiveApprove && confirmMode === "approved" ? (
        <LiveApproveDialog
          request={request}
          submitting={submitting}
          error={error}
          onCancel={closeModal}
          onConfirm={() => {
            void submitReview("approved", "Approve");
          }}
        />
      ) : null}

      {revokeOpen ? (
        <LiveRevokeDialog
          request={request}
          submitting={submitting}
          error={error}
          onCancel={closeModal}
          onConfirm={() => {
            void submitRevoke();
          }}
        />
      ) : null}

      {standardModal ? (
        <div className="review-modal-root" data-testid="admin-review-modal-root">
          <button
            type="button"
            className="review-modal-backdrop"
            aria-label="Close confirmation"
            data-testid="admin-review-modal-backdrop"
            onClick={closeModal}
          />
          <div
            ref={modalRef}
            className="review-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            data-testid="admin-review-modal"
            tabIndex={-1}
          >
            <h3 id={titleId} className="review-modal__title">
              {confirmMode === "approved"
                ? "Approve this request?"
                : "Deny this request?"}
            </h3>
            <p className="review-modal__body">
              {confirmMode === "approved"
                ? "This marks the access request as approved. You can still manage credentials outside the portal."
                : wasContested
                  ? "This marks the access request as denied. The applicant will see the reason you provide and cannot contest again."
                  : "This marks the access request as denied. The applicant will see the reason you provide."}
            </p>

            {confirmMode === "rejected" ? (
              <div className="review-modal__field">
                <label className="review-modal__label" htmlFor={reasonId}>
                  Reason
                </label>
                <div className="review-modal__textarea-wrap">
                  <textarea
                    id={reasonId}
                    className="review-modal__textarea"
                    data-testid="admin-review-reason"
                    value={reason}
                    maxLength={MAX_REJECTION_REASON_LENGTH}
                    rows={4}
                    aria-invalid={showReasonError}
                    aria-describedby={
                      showReasonError ? reasonErrorId : reasonHintId
                    }
                    onChange={(event) => {
                      setReason(event.target.value);
                      setReasonDirty(true);
                    }}
                    onBlur={() => {
                      setReasonDirty(true);
                    }}
                  />
                  <span
                    className={`review-modal__counter${
                      trimLen >= MAX_REJECTION_REASON_LENGTH
                        ? " review-modal__counter--max"
                        : ""
                    }`}
                    data-testid="admin-review-reason-counter"
                  >
                    {reason.length} / {MAX_REJECTION_REASON_LENGTH}
                  </span>
                </div>
                {showReasonError ? (
                  <p
                    id={reasonErrorId}
                    className="review-modal__error"
                    data-testid="admin-review-reason-error"
                    role="alert"
                  >
                    Enter at least {MIN_REJECTION_REASON_LENGTH} characters
                  </p>
                ) : (
                  <p
                    id={reasonHintId}
                    className="review-modal__hint"
                    data-testid="admin-review-reason-hint"
                  >
                    Minimum {MIN_REJECTION_REASON_LENGTH} characters
                  </p>
                )}
              </div>
            ) : null}

            {error ? (
              <ApiErrorNotice
                error={error}
                className="review-modal__error"
                testId="admin-review-error"
              />
            ) : null}

            <div className="review-modal__actions">
              <button
                type="button"
                className="review-modal__btn review-modal__btn--secondary"
                data-testid="admin-review-modal-cancel"
                disabled={submitting}
                onClick={closeModal}
              >
                Cancel
              </button>
              {confirmMode === "approved" ? (
                <button
                  type="button"
                  className="review-modal__btn review-modal__btn--approve"
                  data-testid="admin-review-modal-confirm"
                  disabled={submitting}
                  onClick={() => {
                    void submitReview("approved");
                  }}
                >
                  {submitting ? "Approving…" : "Approve request"}
                </button>
              ) : (
                <button
                  type="button"
                  className="review-modal__btn review-modal__btn--deny"
                  data-testid="admin-review-modal-confirm"
                  disabled={submitting || !canDeny}
                  onClick={() => {
                    void submitReview("rejected");
                  }}
                >
                  {submitting ? "Denying…" : "Deny request"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
